import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener('change', this.#handleChange);
    document.addEventListener('cart:update', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
  }

  /** @param {Event} e */
  #handleChange = (e) => {
    const toggle = /** @type {HTMLInputElement} */ (e.target);
    if (!toggle.matches?.('.cart-expedite__toggle')) return;
    const card = toggle.closest('.cart-expedite');
    if (!card) return;
    const variantId = card.dataset.expediteVariantId;
    const sectionId = card.dataset.expediteSectionId || '';
    if (!variantId) return;

    const cartAddUrl = (this.dataset.cartAddUrl || window.Theme?.routes?.cart_add_url || '/cart/add');
    const addUrl = cartAddUrl.includes('.js') ? cartAddUrl : `${cartAddUrl}.js`;
    const cartUrl = (this.dataset.cartUrl || window.Theme?.routes?.cart_url || '/cart');
    const cartUrlJson = cartUrl.includes('.js') ? cartUrl : `${cartUrl}.js`;
    const cartChangeUrl = this.dataset.cartChangeUrl || window.Theme?.routes?.cart_change_url || '/cart/change';

    const setBusy = (busy) => {
      toggle.disabled = busy;
      card.classList.toggle('cart-expedite--busy', busy);
    };
    const refreshSection = () => {
      if (!sectionId || typeof sectionRenderer?.renderSection !== 'function') return;
      sectionRenderer.renderSection(sectionId, { cache: false });
    };

    setBusy(true);
    if (toggle.checked) {
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10) || variantId, quantity: 1 }],
          sections: sectionId ? sectionId : undefined,
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.status && data.status !== 200) {
            toggle.checked = false;
            if (data.message) console.warn(data.message);
          } else {
            if (data.sections?.[sectionId] && typeof morphSection === 'function') {
              morphSection(sectionId, data.sections[sectionId]);
            } else {
              refreshSection();
            }
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: { resource: data, sourceId: 'cart-expedite', data: { sections: data.sections } },
              })
            );
          }
        })
        .catch(() => { toggle.checked = false; })
        .finally(() => setBusy(false));
    } else {
      fetch(cartUrlJson)
        .then((r) => r.json())
        .then((cart) => {
          let lineNum = 0;
          if (cart?.items?.length) {
            for (let i = 0; i < cart.items.length; i++) {
              if (String(cart.items[i].variant_id) === String(variantId)) {
                lineNum = i + 1;
                break;
              }
            }
          }
          if (lineNum === 0) {
            setBusy(false);
            return;
          }
          return fetch(cartChangeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              line: lineNum,
              quantity: 0,
              sections: sectionId || undefined,
              sections_url: window.location.pathname,
            }),
          }).then((r) => r.json());
        })
        .then((data) => {
          if (!data) return;
          if (data.sections?.[sectionId] && typeof morphSection === 'function') {
            morphSection(sectionId, data.sections[sectionId]);
          } else {
            refreshSection();
          }
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: { resource: data, sourceId: 'cart-expedite', data: { sections: data.sections } },
            })
          );
        })
        .catch(() => { toggle.checked = true; })
        .finally(() => setBusy(false));
    }
  };

  /** @param {CustomEvent & { detail?: { resource?: { items?: { variant_id: number }[] } } }} e */
  #handleCartUpdate = (e) => {
    const resource = e.detail?.resource;
    if (!resource) return;
    const toggle = this.querySelector('.cart-expedite__toggle');
    const card = this.querySelector('.cart-expedite');
    if (!toggle || !card) return;
    const variantId = card.dataset.expediteVariantId;
    if (!variantId) return;
    const inCart = (resource.items || []).some((item) => String(item.variant_id) === String(variantId));
    toggle.checked = inCart;
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
